// PromoOverlay.jsx
import React, { useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';

export default function PromoOverlay({
  open,
  onClose,
  href = '#',
  imageSrc,
  imageAlt = '',
  title = 'Limited-time offer',
  description = 'Don’t miss this chance to save.',
  ctaText = 'View Offer',
  /** Optional: tweak sizes */
  maxWidth = 720
}) {
  const backdropRef = useRef(null);
  const cardRef = useRef(null);

  // Close on ESC
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  // Prevent background scroll when open
  useEffect(() => {
    if (!open) return;
    const { overflow, paddingRight } = document.body.style;
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    document.body.style.overflow = 'hidden';
    if (scrollbarWidth > 0) {
      document.body.style.paddingRight = `${scrollbarWidth}px`;
    }
    return () => {
      document.body.style.overflow = overflow || '';
      document.body.style.paddingRight = paddingRight || '';
    };
  }, [open]);

  if (!open) return null;

  const portalTarget =
    document.getElementById('promo-overlay-root') ||
    document.body;

  const handleBackdropClick = (e) => {
    // Only close if clicking the backdrop, not children
    if (e.target === backdropRef.current) {
      onClose?.();
    }
  };

  const navigateToPromo = () => {
    // Prefer SPA navigation if available
    try {
      if (href.startsWith('http')) {
        window.location.href = href;
      } else {
        // If using React Router (optional): replace with your navigate()
        window.location.assign(href);
      }
    } catch {
      window.location.href = href;
    }
  };

  const stop = (e) => e.stopPropagation();

  return ReactDOM.createPortal(
    <>
      {/* Component-scoped styles */}
      <style>{`
        .po-backdrop {
          position: fixed;
          inset: 0;
          background: rgba(0,0,0,0.6);
          backdrop-filter: blur(2px);
          display: grid;
          place-items: center;
          z-index: 9999;
        }
        .po-card {
          width: min(92vw, ${maxWidth}px);
          background: #0b0b0d; /* dark mode base */
          color: #e6e7eb;
          border: 1px solid rgba(255,255,255,0.08);
          border-radius: 16px;
          box-shadow: 0 20px 60px rgba(0,0,0,0.5);
          overflow: hidden;
          cursor: pointer; /* whole card is clickable */
          transform: translateY(6px);
          animation: po-rise 180ms ease-out forwards;
        }
        @keyframes po-rise {
          to { transform: translateY(0); }
        }
        .po-layout {
          display: grid;
          grid-template-columns: 1fr;
        }
        .po-media {
          position: relative;
          aspect-ratio: 16/9;
          overflow: hidden;
          background: #111216;
        }
        .po-media img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          display: block;
        }
        .po-body {
          padding: 18px 18px 20px;
          display: grid;
          gap: 10px;
        }
        .po-title {
          font-size: 1.25rem;
          font-weight: 700;
          line-height: 1.2;
          letter-spacing: 0.2px;
        }
        .po-desc {
          color: #b7bac4;
          font-size: 0.98rem;
          line-height: 1.5;
        }
        .po-actions {
          display: flex;
          gap: 12px;
          align-items: center;
          margin-top: 4px;
        }
        .po-cta {
          position: relative;
          padding: 10px 16px;
          border-radius: 999px;
          font-weight: 700;
          border: none;
          background: #ffffff;
          color: #0b0b0d;
          cursor: pointer;
        }
        /* animated border ring on hover/focus */
        .po-cta:before {
          content: '';
          position: absolute;
          inset: -2px;
          border-radius: 999px;
          border: 2px solid rgba(255,255,255,0.15);
          opacity: 0;
          transform: scale(0.98);
          transition: opacity 180ms ease, transform 180ms ease;
          pointer-events: none;
        }
        .po-cta:hover:before,
        .po-cta:focus-visible:before {
          opacity: 1;
          transform: scale(1);
        }
        .po-close {
          margin-left: auto;
          padding: 8px 12px;
          background: transparent;
          border: 1px solid rgba(255,255,255,0.18);
          color: #e6e7eb;
          border-radius: 10px;
          cursor: pointer;
        }
        .po-close:hover, .po-close:focus-visible {
          border-color: rgba(255,255,255,0.35);
        }

        /* Larger screens: side-by-side */
        @media (min-width: 720px) {
          .po-layout {
            grid-template-columns: 1fr 1.1fr;
          }
          .po-media {
            aspect-ratio: auto;
            min-height: 260px;
          }
          .po-body {
            padding: 22px 22px 22px 20px;
          }
          .po-title {
            font-size: 1.4rem;
          }
        }
      `}</style>

      <div
        className="po-backdrop"
        ref={backdropRef}
        onClick={handleBackdropClick}
        aria-modal="true"
        role="dialog"
      >
        <div
          className="po-card"
          ref={cardRef}
          onClick={navigateToPromo}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              navigateToPromo();
            }
          }}
        >
          <div className="po-layout">
            {imageSrc ? (
              <div className="po-media" aria-hidden={!imageAlt}>
                <img src={imageSrc} alt={imageAlt} />
              </div>
            ) : null}

            <div className="po-body" onClick={stop}>
              <div className="po-title">{title}</div>
              <div className="po-desc">{description}</div>

              <div className="po-actions">
                <button
                  type="button"
                  className="po-cta"
                  onClick={navigateToPromo}
                  aria-label={ctaText}
                >
                  {ctaText}
                </button>
                <button
                  type="button"
                  className="po-close"
                  onClick={(e) => {
                    e.stopPropagation();
                    onClose?.();
                  }}
                  aria-label="Close promotion"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>,
    portalTarget
  );
}